// src/components/Finanzas/utils/facturaMath.js
// Formatters and label maps for the Finanzas module — and nothing else.
//
// NO MONEY ARITHMETIC IN REACT. Every total, average, rate and ratio already
// exists as a column on the v_finanzas_* views. If you are about to add up
// amounts across rows here, you are re-deriving a figure from line items, which
// is exactly the drift those views exist to prevent. Add a column to the view
// instead. The only math allowed here is formatting a single value.

import { PAYMENT_METHOD_META } from './paymentColors';

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const oneDecimal = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

// Postgres numerics arrive as strings from PostgREST; null/garbage → null.
const toNum = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** 1234.5 → '$1,234.50'. Always two decimals; null → '$0.00'. */
export function fmtUsd(v) {
  return usd.format(toNum(v) ?? 0);
}

/** Fraction → percent: 0.7143 → '71.4%'. null → '—'. */
export function fmtPct(v) {
  const n = toNum(v);
  if (n === null) return '—';
  return `${oneDecimal.format(n * 100)}%`;
}

/** 235.4 → '235.4 lb'. null → '—'. */
export function fmtLb(v) {
  const n = toNum(v);
  if (n === null) return '—';
  return `${oneDecimal.format(n)} lb`;
}

/** 3.5 → '$3.50/lb'. null → '—'. */
export function fmtRate(v) {
  const n = toNum(v);
  if (n === null) return '—';
  return `${usd.format(n)}/lb`;
}

// tipo is never null: casillero, encomienda or sin_clasificar. sin_clasificar is
// a real bucket with live invoices in it — keep it visible, never fold it away.
export const TIPO_META = {
  casillero:      { label: 'Casillero',      color: '#0EA5E9' },
  encomienda:     { label: 'Encomienda',     color: '#8B5CF6' },
  sin_clasificar: { label: 'Sin clasificar', color: '#94A3B8' },
};

export const TIPO_ORDER = ['casillero', 'encomienda', 'sin_clasificar'];

const UNKNOWN_COLOR = '#CBD5E1';

/** Metadata for a tipo; an unexpected key shows as itself rather than vanishing. */
export function tipoMeta(t) {
  return TIPO_META[t] || { label: t ? String(t) : 'Desconocido', color: UNKNOWN_COLOR };
}

/** Metadata for a payment method; an unexpected key shows as itself rather than vanishing. */
export function metodoMeta(m) {
  return PAYMENT_METHOD_META[m] || { label: m ? String(m) : 'Desconocido', color: UNKNOWN_COLOR };
}

// Invoice breakdown, one entry per v_finanzas_factura column. Each column is the
// view's own total for a family of invoice_line_items.line_type values, so the
// drill shows line items grouped by type without reading the base table.
// descuento arrives already negative.
export const DESGLOSE_META = {
  flete:     { label: 'Flete',      lineTypes: ['freight_casillero', 'freight_encomienda', 'freight_documento'], pasante: false },
  recargo:   { label: 'Recargos',   lineTypes: ['surcharge_under_5lb', 'surcharge_under_10lb'],                  pasante: false },
  ajuste:    { label: 'Ajustes',    lineTypes: ['adjustment'],                                                   pasante: false },
  descuento: { label: 'Descuentos', lineTypes: ['discount'],                                                     pasante: false },
  dai:       { label: 'DAI',        lineTypes: ['dai'],                                                          pasante: true  },
  iva:       { label: 'IVA',        lineTypes: ['iva'],                                                          pasante: true  },
};

export const DESGLOSE_ORDER = ['flete', 'recargo', 'ajuste', 'descuento', 'dai', 'iva'];

export const STATUS_META = {
  draft:   { label: 'Borrador', bg: '#fef3c7', color: '#92400e' },
  sent:    { label: 'Enviada',  bg: '#dbeafe', color: '#1d4ed8' },
  partial: { label: 'Parcial',  bg: '#d9f99d', color: '#3f6212' },
  paid:    { label: 'Pagada',   bg: '#dcfce7', color: '#15803d' },
  void:    { label: 'Anulada',  bg: '#f3f4f6', color: '#6b7280' },
};

/** Metadata for an invoice status; an unexpected key shows as itself. */
export function statusMeta(s) {
  return STATUS_META[s] || { label: s ? String(s) : 'Desconocido', bg: '#f3f4f6', color: '#6b7280' };
}

/** Which document proves a package, by tipo — casillero is photographed, encomienda carries a receipt. */
export function documentoLabel(tipo) {
  if (tipo === 'casillero') return 'Foto de bodega';
  if (tipo === 'encomienda') return 'Recibo';
  return 'Documento';
}

export const INGRESO_VS_PASANTES_NOTE =
  'Pasantes (DAI + IVA) se cobran por cuenta de la DGA y no son ingreso del tenant.';
